import React from 'react';

import styled from 'styled-components';

import { useTheme } from '@techstyle/react-components';

import { Model as ModelTypes } from '../../types/modelTypes';
import { Component as Carousel } from '../Carousel';
import NewThemeIcon from '../NewThemeIcon/NewThemeIcon';
import { Component as ProductDetailPlayButton } from '../ProductDetailPlayButton';
import { Component as ProductImage } from '../ProductImage';
import ProductVideo, { VideoCoverSizes } from '../ProductVideo/ProductVideo';

const MINI_THUMBNAIL_WIDTH = 67;
const THUMBNAIL_WIDTH = 88;
const ARROW_OFFSET = 10;

const StyledCarousel = styled(Carousel)`
  display: none;

  .slick-arrow.slick-disabled {
    display: none !important;
  }

  .slick-arrow {
    top: auto;
    transform: translateX(-50%);
    margin-top: 0;
  }

  /* When there are fewer slides than slidesToShow, .slick-list does not have a
  * height inline-style, so .slick-track’s ridiculously tall height creates too
  * much negative space below. this css hack resets it to auto for everything.
  * see https://github.com/akiran/react-slick/pull/1383
  */
  .slick-track {
    height: auto !important;

    .slick-slide > div {
      width: 100%;
    }
  }

  @media (min-width: ${props => props.theme.breakpoints.xlarge}px) {
    float: left;
    position: relative;

    display: block;
    // check if the negative margin-top is necessary
    margin-top: ${-1 * (ARROW_OFFSET + 2)}px;
    margin-bottom: -20px;
    margin-right: ${props => props.theme.spacing.moderate}px;
    margin-left: auto;
    padding-top: 2px;
    padding-bottom: 2px;
    width: 104px;

    .slick-slide {
      align-items: stretch;
      display: flex;
      padding-top: 10px;
      padding-bottom: 10px;
      min-height: ${MINI_THUMBNAIL_WIDTH}px;
    }
  }

  @media (min-width: ${props => props.theme.breakpoints.xxlarge}px) {
    .slick-slide {
      min-height: ${THUMBNAIL_WIDTH}px;
    }
  }
`;

const ArrowButton = styled.button`
  border-radius: 0;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  height: 24px;
  min-width: 0;

  background-color: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.black};
  width: 24px;
  max-height: 24px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  &:hover {
    background-color: ${props => props.theme.colors.stone};
  }

  color: ${props => props.theme.colors.default};
  text-align: center;
`;

const PrevButton = styled(ArrowButton).attrs({
  [`aria-label`]: 'Previous',
})`
  top: 2px;

  cursor: n-resize;
`;

const NextButton = styled(ArrowButton).attrs({
  [`aria-label`]: 'Next',
})`
  bottom: 2px;

  cursor: s-resize;
`;

type ThumbnailButtonProps = {
  isActive: boolean;
};

const ThumbnailButton = styled.button<ThumbnailButtonProps>`
  display: block;
  overflow: hidden;
  position: relative;
  width: 100%;

  background-color: ${props => props.theme.colors.gray150};

  &:after {
    content: ' ';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    box-shadow: 0 0 0 2px inset
      ${props => (props.isActive ? props.theme.colors.default : 'transparent')};
  }

  &:focus {
    outline: 2px solid ${props => props.theme.focusColor};
  }
`;

const VideoThumbnailButton = styled(ThumbnailButton)``;

const VideoThumbnailCover = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: block;
  z-index: 3;
  background-color: transparent;
`;

const ProductThumbnail = styled(ProductImage)`
  display: block;
  height: auto;
  max-width: 100%;
`;

const ProductThumbnailVideo = styled(ProductVideo)`
  display: block;
  height: auto;
  max-width: 100%;
`;

type ThumbnailCarouselProps = {
  model: ModelTypes;
  thumbnailSliderRef: React.RefObject<any>;
  goToSlide: (index: number, event: string) => void;
  activeSlide: number | string;
};

const ThumbnailCarousel = ({
  model,
  thumbnailSliderRef,
  goToSlide,
  activeSlide,
}: ThumbnailCarouselProps) => {
  const angles: Array<number | 'video' | 'laydown'> | null = model.angles;
  // @ts-ignore
  const theme = useTheme();
  const { vimeo_id: vimeoId } = model.product;

  return (
    <StyledCarousel
      dataTestId="ThumbnailCarousel"
      sliderRef={thumbnailSliderRef}
      options={{
        arrows: true,
        dots: false,
        infinite: false,
        slidesToShow: 4,
        vertical: true,
        verticalSwiping: true,
        nextArrow: (
          <NextButton>
            <NewThemeIcon type="caretDown" size={24} />
          </NextButton>
        ),
        prevArrow: (
          <PrevButton>
            <NewThemeIcon type="caretUp" size={24} />
          </PrevButton>
        ),
        responsive: [
          {
            // Large Tablet, Small Desktop
            breakpoint: theme.breakpoints.xxlarge,
            settings: {
              slidesToShow: 4,
            },
          },
          {
            breakpoint: 1290,
            settings: {
              slidesToShow: 4,
            },
          },
          {
            breakpoint: 1200,
            settings: {
              slidesToShow: 4,
            },
          },
        ],
      }}
    >
      {angles.map((angle, i) => {
        const product = model.product;
        const modelIndex = model.modelIndex;

        if (angle === 'video') {
          return (
            <VideoThumbnailButton
              key={`thumbnails-${i}`}
              onMouseOver={() => goToSlide(i, 'hover')}
              onClick={() => goToSlide(i, 'click')}
              isActive={i === activeSlide}
            >
              <ProductThumbnailVideo
                product={model.product}
                vimeoId={vimeoId}
                playIcon={
                  <ProductDetailPlayButton
                    overlay
                    handlePlayButton={() => {}}
                  />
                }
                size={VideoCoverSizes.SMALL}
              />
              <VideoThumbnailCover />
            </VideoThumbnailButton>
          );
        }

        return (
          <ThumbnailButton
            key={`thumbnails-${i}`}
            onMouseOver={() => goToSlide(i, 'hover')}
            onClick={() => goToSlide(i, 'click')}
            isActive={i === activeSlide}
          >
            <ProductThumbnail
              data-autotag="pdpCarouselImages"
              product={product}
              modelIndex={modelIndex}
              angle={angle}
              size={6}
              alt=""
            />
          </ThumbnailButton>
        );
      })}
    </StyledCarousel>
  );
};

export default ThumbnailCarousel;
